import PropTypes from 'prop-types';
import { Box, Tab, Tabs } from '@mui/material';
import useActivity from 'api/useActivity';
import PortalIFrame from 'components/shared/PortalIFrame';
import ProductType, { AVSessionUmbrella } from 'enums/ProductType';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FOCUS_GROUPS_REPORTS_AREAS, createFocusGroupsReportsAreaRelativeURL } from 'utils/activityUrlHelper';
import ActivitiesTabPanel from './activities/ActivitiesTabPanel';
import { Navigate } from 'react-router-dom';
import useActivityRecordings from 'api/useActivityRecordings';
import LoadingPlaceholder from 'components/shared/LoadingPlaceholder';
import RecordingsTabPanel from './recordings/RecordingsTabPanel';
import VoiceTranscriptionsReport from './VoiceTranscriptionsReport/VoiceTranscriptionsReport';
import AIReportsPanel from '../../FocusGroupAIReports/FocusGroupAIReports';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import SessionReportTabPanel from './SessionReport/SessionReportTabPanel';

const StimulusBasedFocusGroupReportsPanel = ({ projectId, activityId: focusGroupId }) => {
    const { t } = useTranslation();
    const { data: activity, isLoadingActivity } = useActivity(focusGroupId);
    const { data: recordings, isLoadingRecordings } = useActivityRecordings(focusGroupId);
    const isLoading = isLoadingActivity || isLoadingRecordings;
    const aiReportsFeatureEnabled = useFeatureIsOn('ai-session-summaries');
    const sessionReportEnabled = useFeatureIsOn('text-chat-session-report');
    const activityType = ProductType[activity?.details?.activityType];
    const isAVSession = AVSessionUmbrella.includes(activityType);
    const showAiReportsTab = aiReportsFeatureEnabled && isAVSession;
    const [selectedTab, setSelectedTab] = useState(aiReportsFeatureEnabled ? 0 : 1);

    useEffect(() => {
        if (!showAiReportsTab && selectedTab === 0) {
            setSelectedTab(1);
        }
    }, [showAiReportsTab, selectedTab]);

    const voiceTranscriptionsTabUri = createFocusGroupsReportsAreaRelativeURL(activityType, projectId, focusGroupId, FOCUS_GROUPS_REPORTS_AREAS.VoiceTranscriptions);

    if (!focusGroupId){
        return null;
    } else if (isLoading) {
        return <LoadingPlaceholder height={'auto'} />;
    }

    const chatTabUri = createFocusGroupsReportsAreaRelativeURL(activityType, projectId, focusGroupId, FOCUS_GROUPS_REPORTS_AREAS.Details);

    return <Box>
        <Tabs
            onChange={(e, value) => setSelectedTab(value)}
            value={selectedTab}
        >
            { showAiReportsTab && <Tab value={0} label={'AI Reports'} /> }
            <Tab value={1} label={t('reporting.tabs.participationAndChat')}  />
            <Tab value={2} label={t('reporting.tabs.activityResults')} />
            {isAVSession && <Tab value={3} label={t('reporting.tabs.textAnalysis')} /> }
            { recordings?.length > 0 && <Tab value={4} label={t('reporting.tabs.recordings')} /> }
            { isAVSession && <Tab value={5} label={t('reporting.tabs.voiceTranscriptions')} /> }
            { !isAVSession && sessionReportEnabled && <Tab value={6} label={'Session Report'} /> }
        </Tabs>
        {selectedTab === 0 && showAiReportsTab && <AIReportsPanel focusGroupId={focusGroupId} />}
        {selectedTab === 1 && <Box value={1} sx={{ padding: 0 }}>
            <PortalIFrame
                containerProps={{ disableGutters: true }}
                id={'activityReportsIframe'}
                frameInfo={{hideReportTabs: true}}
                relativeUrl={chatTabUri} />
        </Box> }
        {selectedTab === 2 && <ActivitiesTabPanel value={2} focusGroupId={focusGroupId} /> }
        {selectedTab === 3 && <Navigate to={`/activity/${focusGroupId}/textAnalysis`} /> }
        {selectedTab === 4 && <RecordingsTabPanel focusGroupId={focusGroupId} /> }
        {selectedTab === 5 && <Box value={5} sx={{ padding: 0 }}>
            <VoiceTranscriptionsReport
                focusGroupId={focusGroupId}
                voiceTranscriptionsTabUri={voiceTranscriptionsTabUri}
            />
        </Box> }
        {selectedTab === 6 && sessionReportEnabled && <SessionReportTabPanel focusGroupId={focusGroupId} />}
    </Box>;
};

StimulusBasedFocusGroupReportsPanel.propTypes = {
    projectId: PropTypes.string,
    activityId: PropTypes.string
};

export default StimulusBasedFocusGroupReportsPanel;
